import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useModuleSync } from '@/hooks/useModuleSync';
import { useAuthContext } from '@/contexts/AuthContext';

export interface Booking {
  id: string;
  type: 'einnahme' | 'ausgabe';
  amount: number;
  description: string;
  category: string | null;
  booking_date: string;
  notes: string | null;
  is_planned: boolean;
  project_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface BookingInsert {
  type: 'einnahme' | 'ausgabe';
  amount: number;
  description: string;
  category?: string;
  booking_date?: string;
  notes?: string;
  is_planned?: boolean;
  project_id?: string;
}

export const fetchBookingsFromDb = async (): Promise<Booking[]> => {
  const { data, error } = await supabase
    .from('bookings')
    .select('*')
    .order('booking_date', { ascending: false });

  if (error) throw error;
  return (data as Booking[]) || [];
};

export const useBookings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { emitEvent, warnings, dismissWarning } = useModuleSync();
  const { user, initialized } = useAuthContext();

  const { data: bookings = [], isLoading: loading, refetch: fetchBookings } = useQuery({
    queryKey: ['bookings'],
    queryFn: fetchBookingsFromDb,
    enabled: !!user && initialized,
  });

  const createBooking = async (booking: BookingInsert) => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .insert({ 
          ...booking, 
          created_by: user?.id,
          booking_date: booking.booking_date || new Date().toISOString().split('T')[0]
        })
        .select()
        .single();

      if (error) throw error;
      queryClient.setQueryData<Booking[]>(['bookings'], (prev = []) => [data as Booking, ...prev]);
      toast({ title: 'Buchung erstellt', description: 'Die Buchung wurde erfolgreich gespeichert.' });
      emitEvent({ type: 'create', module: 'bookings', data, projectId: data.project_id });
      return data;
    } catch (error: any) {
      console.error('Error creating booking:', error);
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Buchung konnte nicht erstellt werden.',
      });
      throw error;
    }
  };

  const updateBooking = async (id: string, updates: Partial<BookingInsert>) => {
    try {
      const { data, error } = await supabase
        .from('bookings')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      queryClient.setQueryData<Booking[]>(['bookings'], (prev = []) => prev.map((b) => (b.id === id ? (data as Booking) : b)));
      toast({ title: 'Buchung aktualisiert', description: 'Die Änderungen wurden gespeichert.' });
      return data;
    } catch (error: any) {
      console.error('Error updating booking:', error);
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Buchung konnte nicht aktualisiert werden.',
      });
      throw error;
    }
  };

  const deleteBooking = async (id: string) => {
    try {
      const { error } = await supabase.from('bookings').delete().eq('id', id);
      if (error) throw error;
      queryClient.setQueryData<Booking[]>(['bookings'], (prev = []) => prev.filter((b) => b.id !== id));
      toast({ title: 'Buchung gelöscht', description: 'Die Buchung wurde entfernt.' });
    } catch (error: any) {
      console.error('Error deleting booking:', error);
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Buchung konnte nicht gelöscht werden.',
      });
      throw error;
    }
  };

  // Computed values
  const einnahmen = bookings.filter(b => b.type === 'einnahme' && !b.is_planned);
  const ausgaben = bookings.filter(b => b.type === 'ausgabe' && !b.is_planned);
  const geplant = bookings.filter(b => b.is_planned);
  const geplantEinnahmen = bookings.filter(b => b.type === 'einnahme' && b.is_planned);
  const geplantAusgaben = bookings.filter(b => b.type === 'ausgabe' && b.is_planned);
  
  const totalEinnahmen = einnahmen.reduce((sum, b) => sum + Number(b.amount), 0);
  const totalAusgaben = ausgaben.reduce((sum, b) => sum + Number(b.amount), 0);
  const saldo = totalEinnahmen - totalAusgaben;
  
  const totalGeplantEinnahmen = geplantEinnahmen.reduce((sum, b) => sum + Number(b.amount), 0);
  const totalGeplantAusgaben = geplantAusgaben.reduce((sum, b) => sum + Number(b.amount), 0);
  const geplantSaldo = totalGeplantEinnahmen - totalGeplantAusgaben;

  return {
    bookings,
    loading,
    fetchBookings,
    createBooking,
    updateBooking,
    deleteBooking,
    // Computed
    einnahmen,
    ausgaben,
    geplant,
    geplantEinnahmen,
    geplantAusgaben,
    totalEinnahmen,
    totalAusgaben,
    saldo,
    totalGeplantEinnahmen,
    totalGeplantAusgaben,
    geplantSaldo,
    // Module sync
    moduleSyncWarnings: warnings,
    dismissModuleWarning: dismissWarning,
  };
};
